// Intent WebHosting API Client

const API_BASE = '/api';

export function getToken() {
  return localStorage.getItem('intent_web_token');
}

export function setToken(token) {
  if (token) {
    localStorage.setItem('intent_web_token', token);
  } else {
    localStorage.removeItem('intent_web_token');
  }
}

export function removeToken() {
  localStorage.removeItem('intent_web_token');
}

export async function apiRequest(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  
  try {
    const res = await fetch(url, {
      ...options,
      headers,
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      if (res.status === 401 && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/register')) {
        removeToken();
        if (window.location.pathname !== '/authentication/sign-in') {
          window.location.href = '/authentication/sign-in';
        }
      }
      const errMessage = (data.error && data.error.message) || data.message || `Request failed with status ${res.status}`;
      const err = new Error(errMessage);
      err.code = data.error?.code || 'API_ERROR';
      err.status = res.status;
      err.data = data;
      throw err;
    }

    return data;
  } catch (err) {
    throw err;
  }
}

export const api = {
  // Auth
  login: (email, password) => apiRequest('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (email, password, name) => apiRequest('/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name }) }),
  getMe: () => apiRequest('/auth/me'),
  logout: () => apiRequest('/auth/logout', { method: 'POST' }),

  // Dashboard
  getStats: () => apiRequest('/dashboard/stats'),
  getActivity: () => apiRequest('/dashboard/activity'),

  // Sites
  listSites: () => apiRequest('/sites'),
  getSite: (id) => apiRequest(`/sites/${id}`),
  createSite: (siteData) => apiRequest('/sites', { method: 'POST', body: JSON.stringify(siteData) }),
  updateSite: (id, siteData) => apiRequest(`/sites/${id}`, { method: 'PUT', body: JSON.stringify(siteData) }),
  deleteSite: (id) => apiRequest(`/sites/${id}`, { method: 'DELETE' }),

  // Deployments
  listDeployments: () => apiRequest('/deployments'),
  getSiteDeployments: (siteId) => apiRequest(`/deployments/site/${siteId}`),
  getDeploymentLogs: (id) => apiRequest(`/deployments/${id}/logs`),
  redeploySite: (siteId, branch) => apiRequest(`/deployments/site/${siteId}`, { method: 'POST', body: JSON.stringify({ branch }) }),
  rollbackDeployment: (id) => apiRequest(`/deployments/${id}/rollback`, { method: 'POST' }),

  // Domains
  listDomains: () => apiRequest('/domains'),
  attachDomain: (siteId, domain) => apiRequest('/domains', { method: 'POST', body: JSON.stringify({ siteId, domain }) }),
  verifyDomain: (id) => apiRequest(`/domains/${id}/verify`, { method: 'POST' }),
  deleteDomain: (id) => apiRequest(`/domains/${id}`, { method: 'DELETE' }),

  // GitHub
  getGitHubStatus: () => apiRequest('/github/status'),
  getGitHubAuthUrl: () => apiRequest('/github/auth-url'),
  githubCallback: (code, state) => apiRequest('/github/callback', { method: 'POST', body: JSON.stringify({ code, state }) }),
  connectGitHubToken: (token) => apiRequest('/github/connect-token', { method: 'POST', body: JSON.stringify({ token }) }),
  getGitHubRepos: () => apiRequest('/github/repos'),
  getGitHubBranches: (owner, repo) => apiRequest(`/github/repos/${owner}/${repo}/branches`),
  disconnectGitHub: () => apiRequest('/github/disconnect', { method: 'POST' }),
};
